import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import type { TooltipProps } from 'recharts'
import { actualBarShape, BAR_RADIUS } from '@/components/forecast-bar'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { forecastBarStyle, forecastRemainder } from '@/lib/forecast'
import { formatMoney, formatPercent } from '@/lib/utils'
import {
  AXIS_TICK,
  BOOKED_COLOR,
  compactMoney,
  currentMonthForecast,
  GRID_COLOR,
  LOSS_COLOR,
  monthLabel,
  monthYearLabel,
  num,
  optionalNum,
  ORDERS_COLOR,
  PROFIT_COLOR,
  REVENUE_COLOR,
  type HomeMonth,
  type HomeSummary,
  type MonthForecast,
} from './model'

type Point = {
  month: string
  label: string
  revenue: number
  profit: number
  orders: number
  margin: number | null
  bookedMargin: number | null
  // The current month only: its full-month forecast, and the part still to come stacked on its bars.
  forecast: MonthForecast | null
  revenueRest?: number
  profitRest?: number
  ordersRest?: number
}

function points(months: HomeMonth[]): Point[] {
  const forecast = currentMonthForecast(months)
  return months.map((month, index) => {
    const point: Point = {
      month: month.month,
      label: monthLabel(month.month),
      revenue: num(month.total_revenue),
      profit: num(month.total_profit),
      orders: month.order_count ?? 0,
      margin: month.order_count ? optionalNum(month.average_margin) : null,
      bookedMargin: month.order_count ? optionalNum(month.booked_margin) : null,
      forecast: index === months.length - 1 ? forecast : null,
    }
    if (!point.forecast) return point
    return {
      ...point,
      revenueRest: forecastRemainder(point.revenue, point.forecast.revenue),
      profitRest: forecastRemainder(point.profit, point.forecast.profit),
      ordersRest: forecastRemainder(point.orders, point.forecast.orders),
    }
  })
}


function legend(items: { label: string; color: string }[], forecast: boolean) {
  return [
    ...items.map((item) => ({ value: item.label, type: 'circle' as const, color: item.color })),
    ...(forecast
      ? [{ value: 'Forecast', type: 'plainline' as const, color: 'oklch(0.556 0 0)', payload: { strokeDasharray: '4 3' } }]
      : []),
  ]
}

function MonthTooltip({ active, payload }: TooltipProps<number, string>) {
  const point = active ? (payload?.[0]?.payload as Point | undefined) : undefined
  if (!point) return null
  return (
    <div className="rounded-md border bg-popover px-3 py-2 text-xs shadow-md">
      <p className="mb-1 font-medium">{monthYearLabel(point.month)}</p>
      <dl className="grid grid-cols-[auto_auto] gap-x-4 gap-y-0.5 tabular-nums">
        <dt className="text-muted-foreground">Revenue</dt>
        <dd className="text-right">{formatMoney(point.revenue)}</dd>
        <dt className="text-muted-foreground">Profit</dt>
        <dd className="text-right">{formatMoney(point.profit)}</dd>
        <dt className="text-muted-foreground">Margin</dt>
        <dd className="text-right">{formatPercent(point.margin)}</dd>
        <dt className="text-muted-foreground">Margin as booked</dt>
        <dd className="text-right">{formatPercent(point.bookedMargin)}</dd>
        <dt className="text-muted-foreground">Orders</dt>
        <dd className="text-right">{point.orders}</dd>
        {point.forecast ? (
          <>
            <dt className="mt-1.5 border-t pt-1.5 text-muted-foreground">Revenue forecast</dt>
            <dd className="mt-1.5 border-t pt-1.5 text-right">{formatMoney(point.forecast.revenue)}</dd>
            <dt className="text-muted-foreground">Profit forecast</dt>
            <dd className="text-right">{formatMoney(point.forecast.profit)}</dd>
            <dt className="text-muted-foreground">Orders forecast</dt>
            <dd className="text-right">{point.forecast.orders}</dd>
          </>
        ) : null}
      </dl>
    </div>
  )
}

const xAxis = <XAxis dataKey="label" tickLine={false} axisLine={false} tick={AXIS_TICK} minTickGap={4} />
const grid = <CartesianGrid vertical={false} stroke={GRID_COLOR} />

export function RevenueChart({ summary }: { summary: HomeSummary }) {
  const data = points(summary.monthly ?? [])
  const forecast = data.some((point) => point.forecast)
  return (
    <Card className="lg:col-span-2">
      <CardHeader>
        <CardTitle className="text-base">Revenue and Profit by month</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[260px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} barGap={2} barCategoryGap="22%" margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
              {grid}
              {xAxis}
              <YAxis
                tickLine={false}
                axisLine={false}
                tick={AXIS_TICK}
                width={56}
                tickFormatter={(value: number) => compactMoney.format(value)}
              />
              <ReferenceLine y={0} stroke="oklch(0.708 0 0)" />
              <Tooltip content={<MonthTooltip />} cursor={{ fill: 'oklch(0.97 0 0)' }} />
              <Legend
                iconSize={8}
                wrapperStyle={{ fontSize: 12 }}
                payload={legend(
                  [
                    { label: 'Revenue', color: REVENUE_COLOR },
                    { label: 'Profit', color: PROFIT_COLOR },
                  ],
                  forecast,
                )}
              />
              <Bar dataKey="revenue" name="Revenue" stackId="revenue" fill={REVENUE_COLOR} shape={actualBarShape('revenueRest')} maxBarSize={22} isAnimationActive={false} />
              <Bar dataKey="revenueRest" name="Revenue forecast" stackId="revenue" {...forecastBarStyle(REVENUE_COLOR)} radius={BAR_RADIUS} maxBarSize={22} isAnimationActive={false} />
              <Bar dataKey="profit" name="Profit" stackId="profit" fill={PROFIT_COLOR} shape={actualBarShape('profitRest')} maxBarSize={22} isAnimationActive={false}>
                {data.map((point) => (
                  <Cell key={point.month} fill={point.profit < 0 ? LOSS_COLOR : PROFIT_COLOR} />
                ))}
              </Bar>
              <Bar dataKey="profitRest" name="Profit forecast" stackId="profit" {...forecastBarStyle(PROFIT_COLOR)} radius={BAR_RADIUS} maxBarSize={22} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  )
}

export function OrdersChart({ summary }: { summary: HomeSummary }) {
  const data = points(summary.monthly ?? [])
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Orders by month</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[260px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} barCategoryGap="28%" margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
              {grid}
              <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ ...AXIS_TICK, fontSize: 11 }} interval={0} />
              <YAxis tickLine={false} axisLine={false} tick={AXIS_TICK} width={32} allowDecimals={false} />
              <Tooltip content={<MonthTooltip />} cursor={{ fill: 'oklch(0.97 0 0)' }} />
              <Bar dataKey="orders" name="Orders" stackId="orders" fill={ORDERS_COLOR} shape={actualBarShape('ordersRest')} maxBarSize={22} isAnimationActive={false} />
              <Bar dataKey="ordersRest" name="Orders forecast" stackId="orders" {...forecastBarStyle(ORDERS_COLOR)} radius={BAR_RADIUS} maxBarSize={22} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  )
}

export function BookedBilledChart({ summary }: { summary: HomeSummary }) {
  const data = points(summary.monthly ?? [])
  const window = summary.financials_12m
  const booked = optionalNum(window?.booked_margin)
  const billed = optionalNum(window?.average_margin)
  const lost = num(window?.booked_profit) - num(window?.total_profit)
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Margin: booked vs billed</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-x-6 gap-y-2">
          <Figure label="As booked" value={formatPercent(booked)} />
          <Figure label="After manufacturer bills" value={formatPercent(billed)} />
          {lost !== 0 ? (
            <Figure
              label={lost > 0 ? 'Profit lost to billing' : 'Profit gained on billing'}
              value={formatMoney(Math.abs(lost))}
              tone={lost > 0 ? 'text-red-700' : 'text-emerald-700'}
            />
          ) : null}
        </div>
        <div className="h-[200px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
              {grid}
              {xAxis}
              <YAxis
                tickLine={false}
                axisLine={false}
                tick={AXIS_TICK}
                width={44}
                tickFormatter={(value: number) => `${value}%`}
              />
              <ReferenceLine y={0} stroke="oklch(0.708 0 0)" />
              <Tooltip content={<MonthTooltip />} cursor={{ stroke: GRID_COLOR }} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
              <Line
                type="linear"
                dataKey="bookedMargin"
                name="As booked"
                stroke={BOOKED_COLOR}
                strokeWidth={2}
                strokeDasharray="4 3"
                dot={{ r: 3, strokeWidth: 2, fill: 'white' }}
                isAnimationActive={false}
              />
              <Line
                type="linear"
                dataKey="margin"
                name="After manufacturer bills"
                stroke={PROFIT_COLOR}
                strokeWidth={2}
                dot={{ r: 3, strokeWidth: 2, fill: 'white' }}
                activeDot={{ r: 5 }}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  )
}

export function Figure({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-xl font-bold tabular-nums ${tone ?? ''}`}>{value}</p>
    </div>
  )
}
